/* eslint-disable react-hooks/set-state-in-effect -- standard initial-load pattern */
import { useEffect, useState, useCallback } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import Navbar from '@/components/Navbar';
import api from '@/lib/api';
import { Loader2, BookOpen, ShoppingCart } from 'lucide-react';
import { Badge } from '@/components/ui/badge';

/**
 * Public "Livros" catalogue. Returns 404 (rendered as "Secção não disponível")
 * when the platform-wide livros section is still "in preparation".
 */
export default function Livros() {
  const navigate = useNavigate();
  const [status, setStatus] = useState('loading');
  const [books, setBooks] = useState([]);

  useEffect(() => {
    (async () => {
      try {
        const sec = await api.get('/livros/section-status');
        if (sec.data.status !== 'published') {
          setStatus('unavailable');
          return;
        }
        const { data } = await api.get('/livros');
        setBooks(data);
        setStatus('ready');
      } catch {
        setStatus('unavailable');
      }
    })();
  }, []);

  if (status === 'loading') {
    return (
      <div className="min-h-screen bg-background">
        <Navbar />
        <div className="flex items-center justify-center h-96"><Loader2 className="w-8 h-8 animate-spin text-ruby" /></div>
      </div>
    );
  }

  if (status === 'unavailable') {
    return (
      <>
        <Helmet>
          <meta name="robots" content="noindex, nofollow" />
          <title>Não disponível — Intimate</title>
        </Helmet>
        <div className="min-h-screen bg-background">
          <Navbar />
          <div className="max-w-lg mx-auto pt-40 px-4 text-center">
            <BookOpen className="w-12 h-12 text-ruby mx-auto mb-4" />
            <h1 className="text-2xl font-bold text-foreground mb-2">Secção não disponível</h1>
            <p className="text-muted-foreground">A secção de Livros ainda não está publicada.</p>
            <button onClick={() => navigate('/home')} className="mt-6 px-4 py-2 bg-ruby rounded-md text-white">Voltar ao início</button>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <Helmet>
        <title>Livros — Intimate</title>
        <meta name="description" content="Livros que a Intimate apresenta e recomenda." />
      </Helmet>
      <div className="min-h-screen bg-background">
        <Navbar />
        <main className="max-w-6xl mx-auto px-4 pt-24 pb-16">
          <div className="mb-8">
            <h1 className="text-4xl md:text-5xl font-bold text-foreground">Livros</h1>
            <p className="text-muted-foreground text-sm mt-2 max-w-2xl">
              Uma seleção curada. A Intimate apresenta e recomenda — a compra é sempre feita diretamente com o autor ou editora.
            </p>
          </div>

          {books.length === 0 && (
            <p className="text-muted-foreground text-center py-16">Ainda não há livros disponíveis.</p>
          )}

          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4 md:gap-6" data-testid="books-grid">
            {books.map(b => (
              <BookCard key={b.id} book={b} />
            ))}
          </div>
        </main>
      </div>
    </>
  );
}

function BookCard({ book, adminBadge }) {
  return (
    <Link to={`/livros/${book.slug}`} className="group block" data-testid={`book-card-${book.slug}`}>
      <div className="aspect-[2/3] rounded-md overflow-hidden bg-white/5 relative">
        {book.cover_image ? (
          <img src={book.cover_image} alt={book.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform" loading="lazy" />
        ) : (
          <div className="w-full h-full flex items-center justify-center"><BookOpen className="w-8 h-8 text-muted-foreground" /></div>
        )}
        {book.featured && (
          <Badge className="absolute top-2 left-2 bg-gold text-black text-[10px]">Destaque</Badge>
        )}
        {book.is_new_release && (
          <Badge className="absolute top-2 right-2 bg-ruby text-white text-[10px]">Novidade</Badge>
        )}
        {adminBadge && (
          <Badge className="absolute bottom-2 left-2 bg-black/70 text-white text-[10px] uppercase">{adminBadge}</Badge>
        )}
      </div>
      <p className="text-sm font-semibold text-foreground mt-2 line-clamp-2 group-hover:text-ruby transition-colors">{book.title}</p>
      <p className="text-xs text-muted-foreground mt-1 truncate">{book.author?.name || book.author_name || '—'}</p>
    </Link>
  );
}

export { BookCard };
